import { useReduxState } from 'hooks';
import { useFormState } from 'informed';
import { getUserAccessOptions } from 'lib/courses';
import {
  Checkbox,
  Code,
  ColorPicker,
  FormGroup,
  NumberStepper,
  Select,
  SortableArrayList,
  Text
} from 'components/common/form';
import { SiteSettingsCompletionCustomContent, SiteSettingsForm } from '.';

import type { LocaleMessages, ServerConfig } from 'shared-types';

type FormValues = Pick<
  ServerConfig,
  | 'courseAnswersSummaryContent'
  | 'courseCompletionAllowEdit'
  | 'courseCompletionAllowRemoval'
  | 'courseCompletionCustomContent'
  | 'courseCompletionElectronicSignature'
  | 'courseCompletionEmailCustomContent'
  | 'courseCompletionHideRequirements'
  | 'courseCompletionHideResults'
  | 'courseCompletionRequireExpiration'
  | 'courseCompletionRequireWorkload'
  | 'courseEnableChatAnonymousCommentsDefault'
  | 'courseEnableChatDefault'
  | 'courseEnableJsonImportForAdmins'
  | 'courseEnableOpeningHours'
  | 'courseEnableShareCode'
  | 'courseEnableShareQRCode'
  | 'courseEnableSignUpLink'
  | 'courseScormExportFileSizeLimit'
  | 'discussionDefaultCommentsOrder'
  | 'discussionEnableReactions'
  | 'editorBlockTypes'
  | 'editorRichTextUpdateTimeout'
  | 'editorTextColorOptions'
  | 'enableCoursePicture'
  | 'likertEnableResponseRates'
  | 'likertEnableUserAnswersAverage'
  | 'multipleChoiceUseAltFeedbackStyle'
  | 'multipleChoiceEnableRandomizeOptions'
  | 'multipleChoiceEnableResponseRates'
  | 'openQuestionReviewSendNotificationDefault'
  | 'videoRequirementsType'
  | 'videoWatchedEnabled'
  | 'videoWatchedThreshold'
  | 'workloadInDays'
  | 'workloadInDaysHoursPerDay'
> & { courseAccess: Record<string, boolean> };

type SubComponentProps = { messages: LocaleMessages };

const CoursePicture = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.siteSettingsCoursePicture}>
    <Checkbox
      field="enableCoursePicture"
      id="enable-course-picture"
      label={messages.siteSettingsEnableCoursePicture}
    />
  </FormGroup>
);

const CoursePublish = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.commonPublish}>
    <Checkbox
      field="courseEnableOpeningHours"
      id="course-enable-opening-hours"
      label={'Enable opening hours'}
    />
  </FormGroup>
);

const CourseAccess = ({ messages }: SubComponentProps) => (
  <FormGroup
    label={messages.courseAccessLimitOption}
    labelHelpText={messages.courseAccessLimitOptionInfo}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Checkbox
        field="courseAccess.added-only"
        label={messages.settingsAddedUsersOnly}
      />
      <Checkbox
        field="courseAccess.sso"
        label={messages.settingsSingleSignOnUsers}
      />
      <Checkbox
        field="courseAccess.registered"
        label={messages.settingsRegistered}
      />
      <Checkbox
        field="courseAccess.registered-and-guests"
        label={messages.settingsRegisteredAndGuests}
      />
      <Checkbox
        field="courseAccess.guests"
        label={messages.settingsGuestsOnly}
      />
      <Checkbox
        field="courseAccess.request-access"
        label={messages.settingsRequestAccess}
      />
    </div>
  </FormGroup>
);

const ImportAndExport = () => (
  <FormGroup label="Import and export">
    <Checkbox
      field="courseEnableJsonImportForAdmins"
      id="course-enable-json-import-for-admins"
      label="Enable JSON import/export tool for admins"
    />
    <NumberStepper
      field="courseScormExportFileSizeLimit"
      id="course-scorm-export-file-size-limit"
      label={'Set Scorm package size limit in MB'}
      min={500}
      stepValue={500}
      style={{ marginTop: 5 }}
    />
  </FormGroup>
);

const Sharing = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.commonSharing}>
    <Checkbox
      field="courseEnableSignUpLink"
      id="course-enable-sign-up-link"
      label={'Enable course specific sign up link'}
    />
    <Checkbox
      field="courseEnableShareQRCode"
      id="course-enable-share-qr-code"
      label={messages.siteSettingsCourseEnableShareQRCode}
      style={{ marginTop: 5 }}
    />
    <Checkbox
      field="courseEnableShareCode"
      id="course-enable-share-code"
      label={messages.siteSettingsCourseEnableShareCode}
      style={{ marginTop: 5 }}
    />
  </FormGroup>
);

const Chat = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.commonChat}>
    <Checkbox
      field="courseEnableChatDefault"
      id="course-enable-chat-default"
      label={messages.siteSettingsEnableChatDefault}
    />
    <Checkbox
      field="courseEnableChatAnonymousCommentsDefault"
      id="course-enable-chat-anonymous-comments-default"
      label={messages.siteSettingsEnableChatAnonymousCommentsDefault}
      style={{ marginTop: 5 }}
    />
  </FormGroup>
);

const EditorBlockTypes = ({ messages }: SubComponentProps) => (
  <FormGroup label={'Editor block types'} style={{ marginTop: 50 }}>
    <Checkbox
      field="editorBlockTypes.pageBreak"
      id="editor-block-types-pagebreak"
      label={messages.blocksPageBreak}
    />
  </FormGroup>
);

const EditorRichTextUpdateTimeout = () => (
  <FormGroup label={'Editor rich text'}>
    <NumberStepper
      field="editorRichTextUpdateTimeout"
      id="editor-rich-text-update-timeout"
      label={'Editor rich text update timeout'}
      min={0}
      stepValue={500}
    />
  </FormGroup>
);

const TextColorOptions = ({ messages }: SubComponentProps) => (
  <FormGroup
    label={messages.siteSettingsEditorTextColorOptions}
    maxWidth="260px"
    style={{ marginTop: 25 }}
  >
    <SortableArrayList
      field="editorTextColorOptions"
      inputElement={ColorPicker}
    />
  </FormGroup>
);

const Likert = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.blocksLikert} style={{ marginTop: 25 }}>
    <Checkbox
      field="likertEnableResponseRates"
      id="likert-enable-response-rates"
      label={messages.siteSettingsMultipleChoiceEnableResponseRates}
    />
    <Checkbox
      field="likertEnableUserAnswersAverage"
      id="likert-enable-user-answers-average"
      label={'Enable showing average of users own answers'}
      style={{ marginTop: 5 }}
    />
  </FormGroup>
);

const MultipleChoice = ({ messages }: SubComponentProps) => (
  <FormGroup
    label={
      messages.blocksMultipleChoice +
      ' ' +
      messages.commonAnd +
      ' ' +
      messages.blocksSingleChoice
    }
    style={{ marginTop: 25 }}
  >
    <Checkbox
      field="multipleChoiceEnableResponseRates"
      id="multiple-choice-enable-response-rates"
      label={messages.siteSettingsMultipleChoiceEnableResponseRates}
    />

    <Checkbox
      field="multipleChoiceEnableRandomizeOptions"
      id="multiple-choice-enable-randomize-options"
      label="Enable randomizer for options"
      style={{ marginTop: 5 }}
    />

    <Checkbox
      field="multipleChoiceUseAltFeedbackStyle"
      id="multiple-choice-use-alt-feedback-style"
      label={messages.siteSettingsMultipleChoiceUseAltFeedbackStyle}
      style={{ marginTop: 5 }}
    />
  </FormGroup>
);

const OpenQuestion = ({ messages }: SubComponentProps) => (
  <FormGroup label={messages.blocksOpenQuestion} style={{ marginTop: 25 }}>
    <Checkbox
      field="openQuestionReviewSendNotificationDefault"
      id="open-question-reivew-send-notification-default"
      label={'Send review email notification default selected'}
    />
  </FormGroup>
);

const Discussion = ({ messages }: SubComponentProps) => (
  <>
    <FormGroup label={messages.blocksDiscussion}>
      <Checkbox
        field="discussionEnableReactions"
        id="discussion-enable-reactions"
        label={'Enable reactions in discussion comments'}
      />
    </FormGroup>

    <FormGroup label="Sort discussion comments by">
      <Select
        field="discussionDefaultCommentsOrder"
        id="discussion-default-comments-order"
        options={[
          { label: 'Oldest first', value: 'oldest' },
          { label: 'Newest first', value: 'newest' }
        ]}
      />
    </FormGroup>
  </>
);

const Video = ({ messages }: SubComponentProps) => {
  const formState = useFormState<FormValues>();

  return (
    <>
      <FormGroup label={messages.blocksVideo}>
        <Checkbox
          field="videoWatchedEnabled"
          id="video-watched-enabled"
          label="Enabled video watched threshold message"
          style={{ marginBottom: 5 }}
        />

        <NumberStepper
          disabled={!formState.values.videoWatchedEnabled}
          field="videoWatchedThreshold"
          id="video-watched-threshold"
          label="Video watched threshold (%)"
          max={100}
          min={0}
        />
      </FormGroup>

      <FormGroup label="Video progress requirements">
        <Select
          field="videoRequirementsType"
          id="video-requirements-type"
          options={[
            { label: 'Combined', value: 'combined' },
            { label: 'Individual', value: 'individual' }
          ]}
        />
      </FormGroup>
    </>
  );
};

const ElectronicSignature = ({ messages }: SubComponentProps) => (
  <>
    <FormGroup
      label={messages.completionElectronicSignature}
      style={{ marginTop: 50 }}
    >
      <Checkbox
        field="courseCompletionElectronicSignature.isEnabled"
        id="electronic-signature-is-enabled"
        label={'Enable electronic signature'}
      />
    </FormGroup>
    <FormGroup label={'Default text in electronic signature'}>
      <Text
        field="courseCompletionElectronicSignature.defaultText"
        placeholder="I certify that I have completed the course"
      />
    </FormGroup>
    <FormGroup label={'Translated texts'}>
      <Code
        field="courseCompletionElectronicSignature.translatedTexts"
        id="electronic-signature-translated-texts"
        placeholder={JSON.stringify(
          {
            'fi-FI': 'Vakuutan suorittaneeni kurssin'
          },
          null,
          2
        )}
      />
    </FormGroup>
  </>
);

const CustomSummaryContent = ({ messages }: SubComponentProps) => (
  <FormGroup
    label={messages.siteSettingsCourseAnswersSummaryContent}
    style={{ marginTop: 25 }}
  >
    <Code
      field="courseAnswersSummaryContent"
      id="course-answers-summary-content"
      placeholder="<div>Hello</div>"
    />
  </FormGroup>
);

const CourseCompletionSettings = ({ messages }: SubComponentProps) => {
  const { values } = useFormState();

  return (
    <FormGroup
      label={messages.siteSettingsCourseCompletionSettings}
      style={{ marginTop: 25 }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Checkbox
          field="courseCompletionAllowEdit"
          id="course-completion-allow-edit"
          label="Allow completions to be edited"
        />
        <Checkbox
          field="courseCompletionAllowRemoval"
          id="course-completion-allow-removal"
          label="Allow completions to be permanently deleted"
        />
        <Checkbox
          field="courseCompletionRequireExpiration"
          id="course-completion-require-expiration"
          label="Require expiration to be defined for completions"
        />
        <Checkbox
          field="courseCompletionRequireWorkload"
          id="course-completion-require-workload"
          label={messages.siteSettingsCourseCompletionRequireWorkload}
        />
        <Checkbox
          field="courseCompletionHideRequirements"
          id="course-completion-hide-requirements"
          label="Hide requirements from completion form"
        />
        <Checkbox
          field="courseCompletionHideResults"
          id="course-completion-hide-results"
          label="Hide results from completion form"
        />
        <Checkbox
          field="workloadInDays"
          id="workload-in-days"
          label="Add workload in days to excel exports"
        />
        <NumberStepper
          disabled={!values?.workloadInDays}
          field="workloadInDaysHoursPerDay"
          id="workload-in-days-hours-per-day"
          label="Workload hours per day"
          min={1}
          max={24}
          stepValue={1}
        />
      </div>
    </FormGroup>
  );
};

const SiteSettingsCourses = () => {
  const { messages, serverConfig } = useReduxState();

  const updateValuesBeforeSubmit = (values) => {
    const { courseAccess } = values;

    const limitCourseUserAccesOptions = courseAccess
      ? Object.keys(courseAccess)?.reduce((allOptions, option) => {
          if (courseAccess[option]) allOptions.push(option);
          return allOptions;
        }, [])
      : [];

    return {
      ...values,
      editorTextColorOptions: (values.editorTextColorOptions ?? []).filter(
        Boolean // without this, trashing all options does not remove the values
      ),
      limitCourseUserAccesOptions
    };
  };

  const courseAccessOptions = getUserAccessOptions({
    includeSingleSignOn: true,
    isDemoEnvironment: false,
    messages
  });

  const {
    courseAnswersSummaryContent,
    courseCompletionAllowEdit,
    courseCompletionAllowRemoval,
    courseCompletionCustomContent,
    courseCompletionElectronicSignature = {},
    courseCompletionEmailCustomContent,
    courseCompletionHideRequirements,
    courseCompletionHideResults,
    courseCompletionRequireExpiration,
    courseCompletionRequireWorkload,
    courseEnableChatAnonymousCommentsDefault,
    courseEnableChatDefault,
    courseEnableJsonImportForAdmins,
    courseEnableOpeningHours,
    courseEnableShareCode,
    courseEnableShareQRCode,
    courseEnableSignUpLink,
    courseScormExportFileSizeLimit,
    discussionDefaultCommentsOrder,
    discussionEnableReactions,
    editorBlockTypes,
    editorRichTextUpdateTimeout,
    editorTextColorOptions,
    enableCoursePicture,
    likertEnableResponseRates,
    likertEnableUserAnswersAverage,
    limitCourseUserAccesOptions,
    multipleChoiceUseAltFeedbackStyle,
    multipleChoiceEnableRandomizeOptions,
    multipleChoiceEnableResponseRates,
    openQuestionReviewSendNotificationDefault,
    videoRequirementsType,
    videoWatchedEnabled,
    videoWatchedThreshold,
    workloadInDays,
    workloadInDaysHoursPerDay
  } = serverConfig;

  const initialValues = {
    courseAccess: courseAccessOptions?.reduce((allOptions, option) => {
      allOptions[option.value] = limitCourseUserAccesOptions.includes(
        option.value
      );
      return allOptions;
    }, {} as Record<string, boolean>),
    courseAnswersSummaryContent,
    courseCompletionAllowEdit,
    courseCompletionAllowRemoval,
    courseCompletionCustomContent,
    courseCompletionElectronicSignature,
    courseCompletionEmailCustomContent,
    courseCompletionHideRequirements,
    courseCompletionHideResults,
    courseCompletionRequireExpiration,
    courseCompletionRequireWorkload,
    courseEnableChatAnonymousCommentsDefault,
    courseEnableChatDefault,
    courseEnableJsonImportForAdmins,
    courseEnableOpeningHours,
    courseEnableShareCode,
    courseEnableShareQRCode,
    courseEnableSignUpLink,
    courseScormExportFileSizeLimit,
    discussionDefaultCommentsOrder,
    discussionEnableReactions,
    editorBlockTypes,
    editorRichTextUpdateTimeout: editorRichTextUpdateTimeout || 500,
    editorTextColorOptions,
    enableCoursePicture,
    likertEnableResponseRates,
    likertEnableUserAnswersAverage,
    multipleChoiceUseAltFeedbackStyle,
    multipleChoiceEnableRandomizeOptions,
    multipleChoiceEnableResponseRates,
    openQuestionReviewSendNotificationDefault,
    videoRequirementsType,
    videoWatchedEnabled,
    videoWatchedThreshold,
    workloadInDays,
    workloadInDaysHoursPerDay
  } satisfies FormValues;

  return (
    <div className="settings-course">
      <SiteSettingsForm
        initialValues={initialValues}
        title={messages.siteSettingsCoursesAndContent}
        updateValuesBeforeSubmit={updateValuesBeforeSubmit}
      >
        {() => (
          <>
            <CoursePicture messages={messages} />
            <CoursePublish messages={messages} />
            <CourseAccess messages={messages} />
            <ImportAndExport />
            <Sharing messages={messages} />
            <Chat messages={messages} />
            <EditorBlockTypes messages={messages} />
            <EditorRichTextUpdateTimeout />
            <TextColorOptions messages={messages} />
            <Likert messages={messages} />
            <MultipleChoice messages={messages} />
            <OpenQuestion messages={messages} />
            <Discussion messages={messages} />
            <Video messages={messages} />
            <ElectronicSignature messages={messages} />
            <SiteSettingsCompletionCustomContent
              field="courseCompletionCustomContent"
              id="completion-custom-content"
              label={messages.settingsCompletionCustomContent}
            />
            <SiteSettingsCompletionCustomContent
              field="courseCompletionEmailCustomContent"
              id="completion-email-custom-content"
              label={messages.settingsCompletionEmailCustomContent}
            />
            <CustomSummaryContent messages={messages} />
            <CourseCompletionSettings messages={messages} />
          </>
        )}
      </SiteSettingsForm>
    </div>
  );
};

export default SiteSettingsCourses;
